// Context for storing application state data

import { createContext, useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import { AppData } from '../types/misc';

export type AppDataUpdates = Partial<AppData>;

export type AppDataContextType = {
  appdata: AppData;
  saveAppData: (updatedAppData: AppData) => void;
  setAppDataProperties: (updates: AppDataUpdates) => void;
};

const initialAppData: AppData = {
  direction: 'ltr',
  responsiveFontSizes: true,
  theme: 'dark',
  rowsPerPage: 50,
  options_cache: {},
  experimentalMetricsMode: false,
};

export const restoreAppData = (): AppData => {
  let appdata: AppData | null = null;

  try {
    const storedData = window.localStorage.getItem('appdata');

    if (storedData) {
      const tmpappdata = JSON.parse(storedData);
      appdata = { ...initialAppData, ...tmpappdata };
    } else {
      appdata = {
        direction: 'ltr',
        responsiveFontSizes: true,
        theme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        rowsPerPage: 50,
        options_cache: {},
        experimentalMetricsMode: false,
      };
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return appdata || initialAppData;
};

const storeAppData = (appdata: AppData): void => {
  // Store settings in localStorage (shared across tabs)
  window.localStorage.setItem('appdata', JSON.stringify(appdata));
};

export const AppDataContext = createContext<AppDataContextType>({
  appdata: initialAppData,
  saveAppData: () => {},
  setAppDataProperties: () => {},
});

interface AppDataProviderProps {
  children: ReactNode;
}

export const AppDataProvider = ({ children }: AppDataProviderProps) => {
  const [appdata, setAppData] = useState<AppData>(initialAppData);

  useEffect(() => {
    const restoredAppData = restoreAppData();

    if (restoredAppData) {
      // Restore after mount so SSR never reads browser storage.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAppData(restoredAppData);
    }
  }, []);

  const saveAppData = useCallback((updatedAppData: AppData) => {
    setAppData(updatedAppData);
    storeAppData(updatedAppData);
  }, []);

  const setAppDataProperties = useCallback((updates: AppDataUpdates) => {
    setAppData((prev) => {
      const updated = { ...prev, ...updates };
      storeAppData(updated);
      return updated;
    });
  }, []);

  // Memoize the provider value so a re-render that doesn't change appdata (or the
  // stable setters) doesn't force every useAppData() consumer to re-render.
  const value = useMemo(() => ({ appdata, saveAppData, setAppDataProperties }), [appdata, saveAppData, setAppDataProperties]);

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

export const AppDataConsumer = AppDataContext.Consumer;
